import React, { useState, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../components/AuthProvider';
import { ConsentGate, hasUserConsented } from '../components/ConsentGate';
import { useUpload } from '../hooks/useUpload';
import { MAX_FILE_SIZE_BYTES, MAX_ROSTER_FILES, SUPPORTED_FORMATS } from '@auditmypay/shared';

type SupportedFormat = typeof SUPPORTED_FORMATS[number];

interface FileValidationError {
  file: string;
  reason: string;
}

function validateFile(file: File): FileValidationError | null {
  if (!SUPPORTED_FORMATS.includes(file.type as SupportedFormat)) {
    return {
      file: file.name,
      reason: `Unsupported format. Please upload a PDF, PNG, or JPEG file.`,
    };
  }
  if (file.size > MAX_FILE_SIZE_BYTES) {
    const maxMB = MAX_FILE_SIZE_BYTES / (1024 * 1024);
    return {
      file: file.name,
      reason: `File exceeds ${maxMB} MB size limit.`,
    };
  }
  return null;
}

export function Dashboard() {
  const { displayName, handleSignOut } = useAuth();
  const [consented, setConsented] = useState(hasUserConsented());
  const [payslipFile, setPayslipFile] = useState<File | null>(null);
  const [rosterFiles, setRosterFiles] = useState<File[]>([]);
  const [payslipError, setPayslipError] = useState<string | null>(null);
  const [rosterError, setRosterError] = useState<string | null>(null);
  const [retainData, setRetainData] = useState(false);

  const {
    state: uploadState,
    progress,
    auditId,
    errorMessage: uploadErrorMessage,
    isRetryable,
    startUpload,
    reset: resetUpload,
  } = useUpload();

  const payslipInputRef = useRef<HTMLInputElement>(null);
  const rosterInputRef = useRef<HTMLInputElement>(null);

  const acceptFormats = SUPPORTED_FORMATS.join(',');

  const handleConsent = useCallback(() => {
    setConsented(true);
  }, []);

  const handlePayslipChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] ?? null;
    setPayslipError(null);
    if (file) {
      const error = validateFile(file);
      if (error) {
        setPayslipError(error.reason);
        setPayslipFile(null);
        if (payslipInputRef.current) payslipInputRef.current.value = '';
        return;
      }
    }
    setPayslipFile(file);
  };

  const handleRosterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : [];
    setRosterError(null);

    if (files.length === 0) {
      setRosterFiles([]);
      return;
    }

    if (files.length > MAX_ROSTER_FILES) {
      setRosterError(`You can upload a maximum of ${MAX_ROSTER_FILES} roster files.`);
      setRosterFiles([]);
      if (rosterInputRef.current) rosterInputRef.current.value = '';
      return;
    }

    for (const file of files) {
      const error = validateFile(file);
      if (error) {
        setRosterError(`${file.name}: ${error.reason}`);
        setRosterFiles([]);
        if (rosterInputRef.current) rosterInputRef.current.value = '';
        return;
      }
    }

    setRosterFiles(files);
  };

  const isUploading = uploadState !== 'idle' && uploadState !== 'done' && uploadState !== 'error';
  const canSubmit = payslipFile !== null && rosterFiles.length > 0 && !isUploading;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;

    await startUpload(payslipFile, rosterFiles, retainData);
  };

  const handleReset = () => {
    resetUpload();
    setPayslipFile(null);
    setRosterFiles([]);
    setPayslipError(null);
    setRosterError(null);
    setRetainData(false);
    if (payslipInputRef.current) payslipInputRef.current.value = '';
    if (rosterInputRef.current) rosterInputRef.current.value = '';
  };

  if (!consented) {
    return (
      <div className="dashboard-page">
        <header className="dashboard-header" role="banner">
          <h1>AuditMyPay</h1>
          <nav aria-label="Main navigation">
            <Link to="/dashboard" className="nav-link" aria-label="Dashboard">
              Dashboard
            </Link>
            <Link to="/history" className="nav-link" aria-label="Audit history">
              History
            </Link>
            <Link to="/settings" className="nav-link" aria-label="Settings">
              Settings
            </Link>
            <Link to="/account" className="nav-link" aria-label="Account">
              Account
            </Link>
          </nav>
          <nav aria-label="User actions">
            <span className="user-greeting" aria-label="Signed in user">
              {displayName ?? 'User'}
            </span>
            <button
              onClick={handleSignOut}
              className="sign-out-button"
              aria-label="Sign out"
            >
              Sign Out
            </button>
          </nav>
        </header>
        <main role="main" aria-labelledby="consent-heading">
          <ConsentGate onConsent={handleConsent} />
        </main>
      </div>
    );
  }

  return (
    <div className="dashboard-page">
      <header className="dashboard-header" role="banner">
        <h1>AuditMyPay</h1>
        <nav aria-label="Main navigation">
          <Link to="/dashboard" className="nav-link" aria-label="Dashboard">
            Dashboard
          </Link>
          <Link to="/history" className="nav-link" aria-label="Audit history">
            History
          </Link>
          <Link to="/settings" className="nav-link" aria-label="Settings">
            Settings
          </Link>
          <Link to="/account" className="nav-link" aria-label="Account">
            Account
          </Link>
        </nav>
        <nav aria-label="User actions">
          <span className="user-greeting" aria-label="Signed in user">
            {displayName ?? 'User'}
          </span>
          <button
            onClick={handleSignOut}
            className="sign-out-button"
            aria-label="Sign out"
          >
            Sign Out
          </button>
        </nav>
      </header>

      <main role="main" aria-labelledby="dashboard-heading">
        <h2 id="dashboard-heading">Upload Dashboard</h2>
        <p>Upload your payslip and roster to begin an audit.</p>

        <form onSubmit={handleSubmit} aria-label="Document upload form">
          <fieldset disabled={isUploading}>
            <legend className="sr-only">Upload Documents</legend>

            {/* Payslip upload */}
            <div className="upload-field">
              <label htmlFor="payslip-upload" className="upload-label">
                Payslip Document
                <span className="upload-hint">
                  (PDF, PNG, or JPEG — max 10 MB)
                </span>
              </label>
              <input
                ref={payslipInputRef}
                type="file"
                id="payslip-upload"
                accept={acceptFormats}
                onChange={handlePayslipChange}
                aria-describedby="payslip-error"
                aria-invalid={payslipError !== null}
              />
              {payslipError && (
                <p
                  id="payslip-error"
                  className="upload-error"
                  role="alert"
                  aria-live="polite"
                >
                  {payslipError}
                </p>
              )}
              {payslipFile && !payslipError && (
                <p className="upload-success" aria-live="polite">
                  Selected: {payslipFile.name}
                </p>
              )}
            </div>

            {/* Roster upload */}
            <div className="upload-field">
              <label htmlFor="roster-upload" className="upload-label">
                Roster Document(s)
                <span className="upload-hint">
                  (PDF, PNG, or JPEG — max 10 MB each, up to {MAX_ROSTER_FILES} files)
                </span>
              </label>
              <input
                ref={rosterInputRef}
                type="file"
                id="roster-upload"
                accept={acceptFormats}
                multiple
                onChange={handleRosterChange}
                aria-describedby="roster-error"
                aria-invalid={rosterError !== null}
              />
              {rosterError && (
                <p
                  id="roster-error"
                  className="upload-error"
                  role="alert"
                  aria-live="polite"
                >
                  {rosterError}
                </p>
              )}
              {rosterFiles.length > 0 && !rosterError && (
                <p className="upload-success" aria-live="polite">
                  Selected: {rosterFiles.map(f => f.name).join(', ')}
                </p>
              )}
            </div>

            {/* Data retention opt-in */}
            <div className="retention-option">
              <label htmlFor="retain-data" className="retention-label">
                <input
                  type="checkbox"
                  id="retain-data"
                  checked={retainData}
                  onChange={(e) => setRetainData(e.target.checked)}
                  aria-describedby="retention-description"
                />
                <span>Retain my audit results for historical review</span>
              </label>
              <p id="retention-description" className="retention-hint">
                If enabled, your audit report will be stored for up to 24 months
                so you can track pay trends over time. You can revoke this at any
                time. If disabled, all data is deleted immediately after analysis.
              </p>
            </div>

            {/* Submit button */}
            <button
              type="submit"
              disabled={!canSubmit}
              className="upload-submit-button"
              aria-label="Submit documents for audit"
            >
              Submit for Audit
            </button>
          </fieldset>
        </form>

        {/* Progress indicator */}
        {isUploading && (
          <div
            className="processing-indicator"
            role="status"
            aria-live="polite"
            aria-label="Upload progress"
          >
            <p>
              {uploadState === 'initiating' && 'Preparing upload...'}
              {uploadState === 'uploading' && 'Uploading documents...'}
              {uploadState === 'completing' && 'Finalising upload...'}
            </p>
            {uploadState === 'uploading' && (
              <div className="progress-details">
                <div className="progress-bar-container">
                  <label htmlFor="payslip-progress">Payslip: {progress.payslip}%</label>
                  <progress
                    id="payslip-progress"
                    value={progress.payslip}
                    max={100}
                  />
                </div>
                <div className="progress-bar-container">
                  <label htmlFor="roster-progress">Roster: {progress.roster}%</label>
                  <progress
                    id="roster-progress"
                    value={progress.roster}
                    max={100}
                  />
                </div>
                <p className="overall-progress">Overall: {progress.overall}%</p>
              </div>
            )}
          </div>
        )}

        {/* Upload complete */}
        {uploadState === 'done' && auditId && (
          <div className="upload-complete" role="status" aria-live="polite">
            <p>Upload complete. Your audit is being processed.</p>
            <p>Audit ID: <code>{auditId}</code></p>
            <button
              onClick={handleReset}
              className="upload-submit-button"
              aria-label="Start a new audit"
            >
              Start New Audit
            </button>
          </div>
        )}

        {/* Upload error */}
        {uploadState === 'error' && uploadErrorMessage && (
          <div className="upload-error-message" role="alert" aria-live="assertive">
            <p>{uploadErrorMessage}</p>
            {isRetryable && (
              <button
                onClick={handleReset}
                className="upload-submit-button"
                aria-label="Try again"
              >
                Try Again
              </button>
            )}
          </div>
        )}
      </main>
    </div>
  );
}
